/**
 * The document's lines, and what each becomes in Odoo.
 *
 * Quantity, price and line total are all editable: the parser can misread any of
 * them, and the running sum against the printed total is what tells a human
 * whether the document has been read whole.
 */
import type { DraftOrderIntent, InkooporderDocument, InkooporderLine } from '@/api/easykitInkooporder';
import { inputClass, money, number, TOTAL_TOLERANCE } from './InkooporderFormat';

function NumberCell({ label, value, onChange }: { label: string; value: number; onChange: (next: number) => void }) {
  return (
    <input
      aria-label={label}
      type="number"
      step="0.01"
      min="0"
      className={`${inputClass} text-right tabular-nums`}
      value={value}
      onChange={(e) => onChange(Number.isNaN(e.target.valueAsNumber) ? 0 : e.target.valueAsNumber)}
    />
  );
}

interface Props {
  doc: InkooporderDocument;
  intent: DraftOrderIntent | null;
  lineSum: number;
  onPatchLine: (index: number, change: Partial<InkooporderLine>) => void;
}

export function InkooporderLines({ doc, intent, lineSum, onPatchLine }: Props) {
  const orderTotal = intent?.orderTotalExcl ?? null;
  const sumMatches = Math.abs(lineSum - doc.totalExcl) <= TOTAL_TOLERANCE;

  return (
    <section className="rounded-xl border border-gray-200 bg-white p-4">
      <h2 className="text-sm font-bold text-td-navy mb-3">
        Lijnen op het document
        <span className="ml-2 font-normal text-td-muted">({doc.lines.length})</span>
      </h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-td-muted border-b border-gray-200">
              <th className="py-2 pr-3 font-semibold">Artikel</th>
              <th className="py-2 pr-3 font-semibold">Omschrijving</th>
              <th className="py-2 pr-3 font-semibold text-right w-24">Aantal</th>
              <th className="py-2 pr-3 font-semibold text-right w-28">Prijs</th>
              <th className="py-2 font-semibold text-right w-28">Totaal</th>
            </tr>
          </thead>
          <tbody>
            {doc.lines.map((line, i) => {
              // Keyed on the article number, which is what the mapping resolves —
              // two lines of one article share a variant.
              const matched = intent?.lines.find((l) => l.articleNumber === line.articleNumber);
              const repriced = matched && Math.abs(matched.unitPrice - line.unitPrice) > 0.005;
              return (
                <tr key={`${line.articleNumber}-${i}`} className="border-b border-gray-100 align-top">
                  <td className="py-2 pr-3 font-mono text-xs text-td-navy whitespace-nowrap">{line.articleNumber}</td>
                  <td className="py-2 pr-3">
                    <div className="text-td-navy">{line.supplierCode}</div>
                    {matched ? (
                      <div className="text-xs text-emerald-700">→ {matched.variantName}</div>
                    ) : (
                      <div className="text-xs text-td-muted">wordt niet geïmporteerd</div>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <NumberCell
                      label={`Aantal ${line.articleNumber}`}
                      value={line.quantity}
                      onChange={(v) => onPatchLine(i, { quantity: v })}
                    />
                    <div className="text-[11px] text-td-muted text-right mt-0.5">{line.unit}</div>
                  </td>
                  <td className="py-2 pr-3">
                    <NumberCell
                      label={`Prijs ${line.articleNumber}`}
                      value={line.unitPrice}
                      onChange={(v) => onPatchLine(i, { unitPrice: v })}
                    />
                    {repriced && (
                      <div className="text-[11px] text-violet-700 text-right mt-0.5">
                        naar {money.format(matched!.unitPrice)}
                      </div>
                    )}
                  </td>
                  <td className="py-2">
                    <NumberCell
                      label={`Totaal ${line.articleNumber}`}
                      value={line.lineTotal}
                      onChange={(v) => onPatchLine(i, { lineTotal: v })}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="font-bold text-td-navy">
              <td className="pt-3" colSpan={3}>Totaal excl. btw (document)</td>
              <td className="pt-3 text-right text-xs font-normal text-td-muted whitespace-nowrap">
                lijnen: {number.format(lineSum)}
              </td>
              <td className="pt-3 text-right whitespace-nowrap">{money.format(doc.totalExcl)}</td>
            </tr>
            {orderTotal != null && Math.abs(orderTotal - doc.totalExcl) > TOTAL_TOLERANCE && (
              <tr className="font-bold text-violet-800">
                <td className="pt-1" colSpan={4}>Totaal na staffel — dit komt in Odoo</td>
                <td className="pt-1 text-right whitespace-nowrap">{money.format(orderTotal)}</td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
      {!sumMatches && (
        <p className="text-xs text-amber-700 mt-2">
          De lijnen tellen op tot {money.format(lineSum)}, het document zegt {money.format(doc.totalExcl)}.
          Dit blokkeert het importeren.
        </p>
      )}
    </section>
  );
}
